import React from 'react';
import type { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinkPreview from 'components/LinkPreview';

/**
 * Creates ReactMarkdown components with Simplifi typography system
 * Optimized for chat bubbles with compact spacing
 * @param theme - MUI theme object
 * @returns Components object for ReactMarkdown
 */
export const createMarkdownComponents = (theme: Theme) => ({
  p: ({ children }: any) => (
    <Typography
      variant='paragraphRegular'
      component='p'
      sx={{ marginBottom: theme.spacing(0.5), '&:last-child': { marginBottom: 0 } }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }: any) => (
    <Typography
      variant='titleBold'
      component='h1'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(1) }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }: any) => (
    <Typography
      variant='titleMedium'
      component='h2'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(1) }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }: any) => (
    <Typography
      variant='bodyBold'
      component='h3'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.75) }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }: any) => (
    <Typography
      variant='bodyMedium'
      component='h4'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.75) }}
    >
      {children}
    </Typography>
  ),
  h5: ({ children }: any) => (
    <Typography
      variant='paragraphBold'
      component='h5'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}
    >
      {children}
    </Typography>
  ),
  h6: ({ children }: any) => (
    <Typography
      variant='paragraphMedium'
      component='h6'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }: any) => (
    <Typography variant='inherit' component='strong' sx={{ display: 'inline', fontWeight: '500' }}>
      {children}
    </Typography>
  ),
  em: ({ children }: any) => (
    <Typography
      variant='paragraphRegular'
      component='em'
      sx={{ display: 'inline', fontStyle: 'italic', fontWeight: 'inherit' }}
    >
      {children}
    </Typography>
  ),
  code: ({ children }: any) => (
    <Typography
      variant='captionRegular'
      component='code'
      sx={{
        display: 'inline',
        fontFamily: 'monospace',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
      }}
    >
      {children}
    </Typography>
  ),
  pre: ({ children }: any) => {
    // Extract text content from code element if needed
    // ReactMarkdown passes code element as children
    return (
      <Box
        component='pre'
        sx={{
          color: theme.palette.text.contrastText,
          width: '100%',
          backgroundColor: theme.palette.backdrop.dark,
          padding: theme.spacing(0.75),
          borderRadius: theme.shape.borderRadius * 0.25,
          overflow: 'auto',
          marginBottom: theme.spacing(0.5),
          marginTop: theme.spacing(0.5),
          ...theme.typography.captionRegular,
          fontFamily: 'monospace',
          whiteSpace: 'pre',
          '& code': {
            display: 'block',
            backgroundColor: 'transparent',
            padding: 0,
            borderRadius: 0,
            whiteSpace: 'pre',
          },
        }}
      >
        {children}
      </Box>
    );
  },
  ul: ({ children }: any) => (
    <Box
      component='ul'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5), paddingLeft: theme.spacing(2.5) }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }: any) => (
    <Box
      component='ol'
      sx={{ marginBottom: theme.spacing(0.5), marginTop: theme.spacing(0.5), paddingLeft: theme.spacing(2.5) }}
    >
      {children}
    </Box>
  ),
  li: ({ children }: any) => (
    <Typography variant='paragraphRegular' component='li' sx={{ marginBottom: theme.spacing(1) }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }: any) => (
    <Box
      component='blockquote'
      sx={{
        borderLeft: `3px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1.5),
        marginLeft: 0,
        marginRight: 0,
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        fontStyle: 'italic',
      }}
    >
      <Typography variant='paragraphRegular'>{children}</Typography>
    </Box>
  ),
  a: ({ children, href }: any) => (
    <LinkPreview href={href} showFavicon={false}>
      {children}
    </LinkPreview>
  ),
  hr: () => (
    <Box
      component='hr'
      sx={{
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        width: '100%',
      }}
    />
  ),
  table: ({ children }: any) => (
    <Box
      sx={{
        width: '100%',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        overflowX: 'auto',
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.border.light}`,
      }}
    >
      <Box
        component='table'
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }: any) => <Box component='thead'>{children}</Box>,
  tbody: ({ children }: any) => <Box component='tbody'>{children}</Box>,
  tr: ({ children }: any) => (
    <Box
      component='tr'
      sx={{
        display: 'table-row',
        borderBottom: `1px solid ${theme.palette.border.light}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      {children}
    </Box>
  ),
  th: ({ children }: any) => (
    <Typography
      variant='paragraphMedium'
      component='th'
      sx={{
        display: 'table-cell',
        padding: theme.spacing(1, 1.5),
        textAlign: 'left',
        borderBottom: `1px solid ${theme.palette.border.main}`,
        backgroundColor: theme.palette.background.default,
        fontWeight: 500,
        verticalAlign: 'middle',
      }}
    >
      {children}
    </Typography>
  ),
  td: ({ children }: any) => (
    <Typography
      variant='paragraphRegular'
      component='td'
      sx={{
        display: 'table-cell',
        padding: theme.spacing(1, 1.5),
        textAlign: 'left',
        verticalAlign: 'middle',
      }}
    >
      {children}
    </Typography>
  ),
});
